import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calculator, TestTubes, Package, Activity, AlertTriangle, TrendingUp, MessageSquare, MessageCircle, Eye, ArrowRight, Users, Sparkles, Crown } from 'lucide-react';
import ContactAdmin from '@/components/ContactAdmin';


export default function Dashboard() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [stats, setStats] = useState(null);
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [schedulesRes, alertsRes, statsRes, communityRes] = await Promise.all([
        fetch(`${API}/schedules`, { credentials: 'include' }),
        fetch(`${API}/vials/alerts`, { credentials: 'include' }),
        fetch(`${API}/logs/stats`, { credentials: 'include' }),
        fetch(`${API}/community/featured?limit=5`, { credentials: 'include' })
      ]);

      if (schedulesRes.ok) {
        const data = await schedulesRes.json();
        setSchedules(data.filter(s => s.is_active));
      }
      if (alertsRes.ok) {
        setAlerts(await alertsRes.json());
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (communityRes.ok) {
        setCommunity(await communityRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const todaySchedules = schedules.filter(s => {
    const today = new Date().toISOString().split('T')[0];
    return s.next_dose_date === today;
  });

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground">Manage your research peptide protocols</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="p-6 glass-card hover:-translate-y-1 transition-transform duration-200">
          <div className="flex items-center justify-between mb-4">
            <Activity className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-2xl font-bold">{stats?.total_doses || 0}</h3>
          <p className="text-sm text-muted-foreground">Total Doses Logged</p>
        </Card>

        <Card className="p-6 glass-card hover:-translate-y-1 transition-transform duration-200">
          <div className="flex items-center justify-between mb-4">
            <TestTubes className="h-8 w-8 text-success" />
          </div>
          <h3 className="text-2xl font-bold">{Object.keys(stats?.peptide_stats || {}).length}</h3>
          <p className="text-sm text-muted-foreground">Active Peptides</p>
        </Card>

        <Card className="p-6 glass-card hover:-translate-y-1 transition-transform duration-200">
          <div className="flex items-center justify-between mb-4">
            <Package className="h-8 w-8 text-info" />
          </div>
          <h3 className="text-2xl font-bold">{alerts ? alerts.low_stock.length : 0}</h3>
          <p className="text-sm text-muted-foreground">Low Stock Alerts</p>
        </Card>

        <Card className="p-6 glass-card hover:-translate-y-1 transition-transform duration-200">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="h-8 w-8 text-warning" />
          </div>
          <h3 className="text-2xl font-bold">{stats?.recent_30_days || 0}</h3>
          <p className="text-sm text-muted-foreground">Doses (30 days)</p>
        </Card>
      </div>

      {/* Alerts */}
      {alerts && (alerts.low_stock.length > 0 || alerts.expiring_soon.length > 0 || alerts.expired.length > 0) && (
        <div className="mb-8 space-y-3">
          {alerts.expired.length > 0 && (
            <Alert className="border-destructive/50 bg-destructive/10" data-testid="expired-alert">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <AlertDescription className="text-destructive">
                <strong>{alerts.expired.length} vial(s) expired.</strong> Check your inventory.
              </AlertDescription>
            </Alert>
          )}
          {alerts.expiring_soon.length > 0 && (
            <Alert className="border-warning/50 bg-warning/10" data-testid="expiring-soon-alert">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertDescription className="text-warning">
                <strong>{alerts.expiring_soon.length} vial(s) expiring soon</strong> (within 30 days)
              </AlertDescription>
            </Alert>
          )}
          {alerts.low_stock.length > 0 && (
            <Alert className="border-info/50 bg-info/10" data-testid="low-stock-alert">
              <AlertTriangle className="h-4 w-4 text-info" />
              <AlertDescription className="text-info">
                <strong>{alerts.low_stock.length} vial(s) running low.</strong> Consider reordering.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Today's Doses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 glass-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Today's Schedule</h2>
            <Button variant="outline" size="sm" onClick={() => navigate('/scheduler')} data-testid="view-schedule-button">
              View All
            </Button>
          </div>
          {todaySchedules.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No doses scheduled for today</p>
          ) : (
            <div className="space-y-3">
              {todaySchedules.map((schedule) => (
                <div key={schedule.schedule_id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border">
                  <div>
                    <p className="font-medium">{schedule.peptide_name}</p>
                    <p className="text-sm text-muted-foreground">{schedule.frequency} • {schedule.times.join(', ')}</p>
                  </div>
                  <Button size="sm" data-testid={`mark-taken-${schedule.schedule_id}`}>
                    Mark Taken
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 glass-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="outline"
              className="h-32 flex-col space-y-2 hover:-translate-y-1 transition-transform duration-200"
              onClick={() => navigate('/calculator')}
              data-testid="quick-calculator-button"
            >
              <Calculator className="h-8 w-8 text-primary" />
              <span>Calculator</span>
            </Button>
            <Button
              variant="outline"
              className="h-32 flex-col space-y-2 hover:-translate-y-1 transition-transform duration-200"
              onClick={() => navigate('/peptides')}
              data-testid="quick-peptides-button"
            >
              <TestTubes className="h-8 w-8 text-success" />
              <span>Peptides</span>
            </Button>
            <Button
              variant="outline"
              className="h-32 flex-col space-y-2 hover:-translate-y-1 transition-transform duration-200"
              onClick={() => navigate('/inventory')}
              data-testid="quick-inventory-button"
            >
              <Package className="h-8 w-8 text-info" />
              <span>Inventory</span>
            </Button>
            <Button
              variant="outline"
              className="h-32 flex-col space-y-2 hover:-translate-y-1 transition-transform duration-200"
              onClick={() => navigate('/logs')}
              data-testid="quick-logs-button"
            >
              <Activity className="h-8 w-8 text-warning" />
              <span>Log Dose</span>
            </Button>
          </div>

          {/* AI Q&A — premium feature, full-width below the action grid */}
          <button
            type="button"
            onClick={() => navigate('/ai-chat')}
            data-testid="quick-ai-chat-button"
            className="w-full mt-4 p-4 rounded-xl border border-primary/40 bg-gradient-to-r from-primary/10 via-amber-500/5 to-primary/10 hover:border-primary/70 transition-all text-left flex items-center gap-3"
          >
            <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold">Ask PeptideForge AI</p>
                <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                  <Crown className="h-2.5 w-2.5" /> Premium
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Get instant answers on dosing, mechanisms, recovery, longevity — or anything else.
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </button>
        </Card>

        {/* Community Discussions */}
        <Card className="p-6 glass-card" data-testid="dashboard-community-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <h2 className="text-xl font-bold">Live from the Community</h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/forums')}
              data-testid="view-all-forums-button"
              className="text-sm"
            >
              View all
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          {community && (
            <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              {community.total_threads} discussions across {community.categories} categories
            </p>
          )}

          {!community && (
            <div className="space-y-3" data-testid="dashboard-community-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="p-4 rounded-xl border border-border animate-pulse">
                  <div className="h-4 bg-secondary/60 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-secondary/40 rounded w-full" />
                </div>
              ))}
            </div>
          )}

          {community && community.threads.length === 0 && (
            <div className="p-6 rounded-xl border border-dashed border-border text-center text-sm text-muted-foreground">
              No discussions yet — be the first to start one.
            </div>
          )}

          {community && community.threads.length > 0 && (
            <div className="space-y-3" data-testid="dashboard-community-list">
              {community.threads.map((t) => (
                <button
                  key={t.thread_id}
                  type="button"
                  onClick={() => navigate('/forums')}
                  className="w-full text-left p-4 rounded-xl border border-border hover:border-primary/40 hover:bg-secondary/30 transition-colors group"
                  data-testid={`dashboard-thread-${t.thread_id}`}
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium uppercase tracking-wide">
                      <MessageSquare className="h-2.5 w-2.5" />
                      {t.category_name}
                    </span>
                    <span>•</span>
                    <span className="truncate">{t.author_name}</span>
                  </div>
                  <h3 className="font-semibold text-sm md:text-base mb-1 group-hover:text-primary transition-colors line-clamp-1">
                    {t.title}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground line-clamp-2 mb-2">
                    {t.content_preview}
                  </p>
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1">
                        <MessageCircle className="h-3 w-3" />
                        {t.reply_count} {t.reply_count === 1 ? 'reply' : 'replies'}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {t.view_count}
                      </span>
                    </div>
                    {t.tags && t.tags.length > 0 && (
                      <div className="hidden sm:flex items-center gap-1">
                        {t.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="px-1.5 py-0.5 rounded bg-secondary/60 text-secondary-foreground/80 text-[10px]"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Contact admin footer */}
        <div className="flex items-center justify-center pt-2">
          <ContactAdmin />
        </div>
      </div>
    </div>
  );
}
